import { useEffect, useState } from "react";
import { ArrowLeft, BadgeCheck, CalendarClock, HeartPulse, IdCard, Mail, Phone, Wallet } from "lucide-react";
import {
  asArray,
  asNumber,
  asText,
  fieldOf,
  formatValue,
  humanize,
  sectionOf,
  type StudentDossier,
  type StudentSection,
} from "@/lib/profile";

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="cb-row" style={{ gap: "0.75rem", alignItems: "baseline", padding: "0.18rem 0" }}>
      <span className="cb-kv__k" style={{ minWidth: "9.5rem" }}>
        {label}
      </span>
      <span className="cb-kv__v" style={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}>
        {value ?? <span style={{ color: "var(--cb-subtle)" }}>—</span>}
      </span>
    </div>
  );
}

function Card({
  title,
  icon,
  children,
  hint,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <section className="cb-panel">
      <div className="cb-panel__head">
        <span className="cb-panel__title">
          {icon}
          {title}
        </span>
        {hint && <span className="cb-label">{hint}</span>}
      </div>
      <div className="cb-panel__body" style={{ display: "grid", gap: "0.2rem" }}>
        {children}
      </div>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" | "err" }) {
  const color = tone === "err" ? "var(--cb-error)" : tone === "warn" ? "var(--cb-warning)" : "var(--cb-text)";
  return (
    <div style={{ display: "grid", gap: "0.15rem", minWidth: "6.5rem" }}>
      <span className="cb-label">{label}</span>
      <span className="cb-display" style={{ fontSize: "1.35rem", color }}>
        {value}
      </span>
    </div>
  );
}

/** Compact rendering used by the "dados brutos" disclosure. */
function RawValue({ field, value }: { field: string; value: unknown }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: "var(--cb-subtle)" }}>vazio</span>;
    return (
      <div style={{ display: "grid", gap: "0.1rem" }}>
        {value.slice(0, 5).map((item, i) => (
          <span key={i} style={{ color: "var(--cb-muted)" }}>
            {typeof item === "object" && item !== null
              ? Object.entries(item as Record<string, unknown>)
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .slice(0, 6)
                  .map(([k, v]) => `${humanize(k).toLowerCase()}: ${formatValue(k, v) ?? "—"}`)
                  .join(" · ") || "—"
              : (formatValue(field, item) ?? "—")}
          </span>
        ))}
        {value.length > 5 && <span className="cb-label">+{value.length - 5}</span>}
      </div>
    );
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== "",
    );
    if (entries.length === 0) return <span style={{ color: "var(--cb-subtle)" }}>—</span>;
    return (
      <span style={{ color: "var(--cb-muted)" }}>
        {entries.slice(0, 6).map(([k, v]) => `${humanize(k).toLowerCase()}: ${formatValue(k, v) ?? "—"}`).join(" · ")}
      </span>
    );
  }
  return <span>{formatValue(field, value) ?? <span style={{ color: "var(--cb-subtle)" }}>—</span>}</span>;
}

function RawSections({ sections }: { sections: StudentSection[] }) {
  const [open, setOpen] = useState(false);
  const loaded = sections.filter((s) => s.data).length;
  return (
    <section className="cb-panel">
      <div className="cb-panel__head">
        <span className="cb-panel__title">
          <BadgeCheck size={13} />
          Origem dos dados
        </span>
        <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={() => setOpen(!open)}>
          {open ? "Ocultar" : `Mostrar (${loaded}/${sections.length})`}
        </button>
      </div>
      {open && (
        <div className="cb-panel__body" style={{ display: "grid", gap: "1rem" }}>
          {sections.map((section) => {
            const rows =
              section.data && typeof section.data === "object" && !Array.isArray(section.data)
                ? Object.entries(section.data as Record<string, unknown>)
                : null;
            const list = Array.isArray(section.data) ? section.data : null;
            return (
              <div key={section.key} style={{ display: "grid", gap: "0.35rem" }}>
                <div className="cb-row" style={{ gap: "0.4rem" }}>
                  <span className="cb-chip cb-chip--ghost" style={{ fontSize: "0.62rem" }}>
                    {section.status}
                  </span>
                  <span style={{ fontWeight: 500 }}>{section.label}</span>
                </div>
                <span className="cb-label" style={{ fontFamily: "var(--font-mono)", overflowWrap: "anywhere" }}>
                  {section.endpoint}
                </span>
                {section.error ? (
                  <span className="cb-label" style={{ color: "var(--cb-error)" }}>
                    {section.error}
                  </span>
                ) : rows ? (
                  <div className="cb-kv">
                    {rows.map(([k, v]) => (
                      <div key={k} className="cb-row" style={{ gap: "0.6rem", alignItems: "baseline", padding: "0.12rem 0" }}>
                        <span className="cb-kv__k" style={{ minWidth: "10rem" }}>
                          {humanize(k)}
                        </span>
                        <span className="cb-kv__v" style={{ minWidth: 0, flex: 1 }}>
                          <RawValue field={k} value={v} />
                        </span>
                      </div>
                    ))}
                  </div>
                ) : list ? (
                  <RawValue field={section.key} value={list} />
                ) : (
                  <span className="cb-label">—</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function valueOf(dossier: StudentDossier | null, key: string, field: string): string | null {
  return formatValue(field, fieldOf(dossier, key, field));
}

/**
 * Student profile page, reachable by id (`#aluno/<id>`). Aggregates everything the
 * staff endpoints hand over for that id into one app-like view — with the list of
 * source endpoints kept in a disclosure at the bottom.
 */
export function StudentProfilePage({ id, note, onBack }: { id: number; note?: string; onBack: () => void }) {
  const [dossier, setDossier] = useState<StudentDossier | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    void (async () => {
      try {
        const res = await fetch(`/api/staff/students/${id}`, { cache: "no-store" });
        const body = (await res.json().catch(() => null)) as StudentDossier | { error?: string } | null;
        if (!alive) return;
        if (!res.ok) {
          setDossier(null);
          setError((body && "error" in body && body.error) || `HTTP ${res.status}`);
          return;
        }
        setDossier(body as StudentDossier);
      } catch (e) {
        if (alive) {
          setDossier(null);
          setError(e instanceof Error ? e.message : "falha de rede");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const nomeCompleto = valueOf(dossier, "perfil", "nomeCompleto") ?? valueOf(dossier, "cadastro", "nome");
  const foto = asText(fieldOf(dossier, "perfil", "foto"));
  const initial = (nomeCompleto ?? "?").trim().charAt(0).toUpperCase();

  const sessoes = asNumber(fieldOf(dossier, "sessoes", "qtdeSessoes"));
  const reposicoes = asNumber(fieldOf(dossier, "sessoes", "qtdeReposicoes"));
  const saldoDevedor = asNumber(fieldOf(dossier, "saldo", "saldoDevedor"));
  const idade = asNumber(fieldOf(dossier, "corpo", "idade"));

  const contratos = asArray(fieldOf(dossier, "sessoes", "nomesContratos"))
    .map((c) => asText(c))
    .filter((c): c is string => Boolean(c));
  const bloqueios = asArray(sectionOf(dossier, "acessos")?.data).length;
  const niveis = asArray(sectionOf(dossier, "niveis")?.data).length;
  const pendencias = asArray(sectionOf(dossier, "pendencias")?.data).length;
  const lancamentos = asArray(sectionOf(dossier, "lancamentos")?.data);
  const documentos = sectionOf(dossier, "documentos")?.data;
  const documentosData = documentos && typeof documentos === "object" ? (documentos as Record<string, unknown>) : null;
  const avaliacoes = asArray(documentosData?.avaliacoesFisicas).length;
  const evolucoes = asArray(documentosData?.evolucoes).length;

  const naTurma = note ?? null;

  return (
    <div style={{ display: "grid", gap: "0.85rem" }}>
      <section className="cb-panel cb-rise">
        <div className="cb-panel__body" style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={onBack}>
            <ArrowLeft size={14} />
            Voltar
          </button>

          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              overflow: "hidden",
              display: "grid",
              placeItems: "center",
              background: "var(--cb-border2)",
              fontFamily: "var(--font-display)",
              fontSize: "1.6rem",
              flex: "none",
            }}
          >
            {foto ? <img src={foto} alt="" width={64} height={64} style={{ objectFit: "cover" }} /> : initial}
          </div>

          <div style={{ minWidth: 0, display: "grid", gap: "0.3rem" }}>
            <p className="cb-display" style={{ margin: 0, fontSize: "1.7rem" }}>
              {loading ? "carregando…" : (nomeCompleto ?? "Aluno sem nome")}
            </p>
            <div className="cb-row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
              <span className="cb-chip cb-chip--ghost">#{id}</span>
              {valueOf(dossier, "cadastro", "nomeFilial") && (
                <span className="cb-chip cb-chip--ghost">{valueOf(dossier, "cadastro", "nomeFilial")}</span>
              )}
              {valueOf(dossier, "perfil", "nivel") && (
                <span className="cb-chip cb-chip--ghost">nível {valueOf(dossier, "perfil", "nivel")}</span>
              )}
              {naTurma && (
                <span
                  className="cb-chip"
                  style={{
                    color: "var(--cb-warning)",
                    background: "var(--cb-warning-dim)",
                    borderColor: "rgba(255,182,46,.35)",
                  }}
                >
                  {naTurma}
                </span>
              )}
            </div>
          </div>

          <div className="cb-spacer" />

          <div className="cb-row" style={{ gap: "1.1rem", flexWrap: "wrap" }}>
            <Stat label="idade" value={idade !== null ? `${idade}` : "—"} />
            <Stat label="sessões usadas" value={sessoes !== null ? `${sessoes}` : "—"} />
            <Stat label="reposições" value={reposicoes !== null ? `${reposicoes}` : "—"} />
            <Stat
              label="saldo devedor"
              value={saldoDevedor !== null ? saldoDevedor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
              tone={saldoDevedor && saldoDevedor > 0 ? "warn" : undefined}
            />
          </div>
        </div>
      </section>

      {loading && !dossier ? (
        <div className="cb-empty">buscando no app de staff…</div>
      ) : error ? (
        <div className="cb-empty">
          <strong>Não foi possível abrir o perfil</strong>
          <span>{error}</span>
        </div>
      ) : (
        <>
          <div className="cb-grid-2">
            <Card title="Identidade" icon={<IdCard size={13} />}>
              <Field label="Nome completo" value={valueOf(dossier, "perfil", "nomeCompleto")} />
              <Field label="CPF" value={valueOf(dossier, "cadastro", "cpf")} />
              <Field label="Nascimento" value={valueOf(dossier, "cadastro", "dtNascimento")} />
              <Field label="Sexo" value={valueOf(dossier, "cadastro", "sexo")} />
              <Field label="Sexo biológico" value={valueOf(dossier, "corpo", "sexoBiologico")} />
              <Field label="Passaporte" value={valueOf(dossier, "cadastro", "passaporte")} />
              <Field
                label="Último acesso"
                value={valueOf(dossier, "perfil", "dataUltimoAcesso") ?? valueOf(dossier, "perfil", "ultimoAcesso")}
              />
            </Card>

            <Card title="Contato" icon={<Mail size={13} />}>
              <Field label="E-mail" value={valueOf(dossier, "cadastro", "email") ?? valueOf(dossier, "perfil", "email")} />
              <Field
                label="Celular"
                value={valueOf(dossier, "cadastro", "celular") ?? valueOf(dossier, "perfil", "celular")}
              />
              <Field label="Telefone" value={valueOf(dossier, "perfil", "telefone")} />
              <Field label="Endereço" value={valueOf(dossier, "cadastro", "endereco")} />
              <Field label="Número" value={valueOf(dossier, "cadastro", "numero")} />
              <Field label="Bairro" value={valueOf(dossier, "cadastro", "bairro")} />
              <Field label="Cidade" value={valueOf(dossier, "cadastro", "cidade")} />
              <Field label="CEP" value={valueOf(dossier, "cadastro", "cep")} />
            </Card>

            <Card title="Academia" icon={<CalendarClock size={13} />} hint={contratos[0] ?? undefined}>
              <Field label="Sessões usadas" value={valueOf(dossier, "sessoes", "qtdeSessoes")} />
              <Field label="Reposições" value={valueOf(dossier, "sessoes", "qtdeReposicoes")} />
              <Field label="Sessões disponíveis" value={valueOf(dossier, "sessoes", "sessoesDisponiveis")} />
              <Field label="Ilimitado" value={valueOf(dossier, "sessoes", "flIlimitado")} />
              <Field label="Níveis" value={niveis > 0 ? `${niveis} registro(s)` : "nenhum"} />
              <Field label="Bloqueios de acesso" value={bloqueios > 0 ? `${bloqueios} registro(s)` : "nenhum"} />
              <Field
                label="Situação"
                value={
                  [valueOf(dossier, "perfil", "flSuspenso") === "sim" ? "suspenso" : null,
                   valueOf(dossier, "perfil", "flRestrito") === "sim" ? "restrito" : null,
                   valueOf(dossier, "perfil", "flInativo") === "sim" ? "inativo" : null]
                    .filter(Boolean)
                    .join(" · ") || "ativo"
                }
              />
            </Card>

            <Card title="Saúde" icon={<HeartPulse size={13} />}>
              <Field label="Peso" value={valueOf(dossier, "corpo", "peso")} />
              <Field label="Altura" value={valueOf(dossier, "corpo", "altura")} />
              <Field label="Morfologia" value={valueOf(dossier, "corpo", "morfologia")} />
              <Field label="Objetivo" value={valueOf(dossier, "corpo", "objetivoDs") ?? valueOf(dossier, "corpo", "objetivo")} />
              <Field label="Avaliações físicas" value={`${avaliacoes}`} />
              <Field label="Evoluções" value={`${evolucoes}`} />
            </Card>

            <Card title="Financeiro" icon={<Wallet size={13} />}>
              <Field label="Saldo devedor" value={valueOf(dossier, "saldo", "saldoDevedor")} />
              <Field label="Saldo credor" value={valueOf(dossier, "saldo", "saldoCredor")} />
              <Field label="FitCoins" value={valueOf(dossier, "saldo", "fitCoins")} />
              <Field label="Pendências" value={pendencias > 0 ? `${pendencias} lançamento(s)` : "nenhuma"} />
              {lancamentos.slice(0, 3).map((l, i) => {
                const item = l && typeof l === "object" ? (l as Record<string, unknown>) : null;
                return (
                  <Field
                    key={i}
                    label={item ? (asText(item.dsRecebimento) ?? "Lançamento") : "Lançamento"}
                    value={
                      item
                        ? [
                            formatValue("valor", item.valor) ? `R$ ${formatValue("valor", item.valor)}` : null,
                            formatValue("dtVencimento", item.dtVencimento)
                              ? `vence ${formatValue("dtVencimento", item.dtVencimento)}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"
                        : null
                    }
                  />
                );
              })}
              {lancamentos.length > 3 && <Field label="" value={`+${lancamentos.length - 3} lançamento(s)`} />}
            </Card>
          </div>

          <p className="cb-label" style={{ textAlign: "center", color: "var(--cb-subtle)" }}>
            <Phone size={11} style={{ verticalAlign: "-1px" }} /> dados lidos do app de staff com o token de aluno —
            CPF mascarado, nada é gravado em banco
          </p>

          <div className="cb-row">
            <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={() => setShowRaw(!showRaw)}>
              {showRaw ? "Ocultar dados brutos" : "Ver dados brutos por seção"}
            </button>
          </div>

          {showRaw && <RawSections sections={dossier?.sections ?? []} />}
        </>
      )}
    </div>
  );
}
